<script setup lang="ts">
import { ref } from "vue";
import type { ImageProps } from "./image.types";
import { useImage } from "./useImage";

defineOptions({ name: "EImage" });

const props = withDefaults(defineProps<ImageProps>(), {
  lazy: false,
  autoSizes: false,
  rounded: undefined,
  skeleton: undefined,
});

const vectorRef = ref<HTMLElement | null>(null);

const {
  resolvedType,
  mediaClasses,
  wrapperClasses,
  imgSrc,
  srcsetAttr,
  sizesAttr,
  fetchPriorityAttr,
  loadingAttr,
  showSkeleton,
  skeletonClasses,
  onImgLoad,
} = useImage({ props, vectorRef });
</script>

<template>
  <div :class="wrapperClasses" class="e-image">
    <img
      v-if="resolvedType === 'image'"
      part="base"
      :src="imgSrc"
      :alt="props.alt ?? ''"
      :width="props.width"
      :height="props.height"
      :srcset="srcsetAttr"
      :sizes="sizesAttr"
      :loading="loadingAttr"
      :fetchpriority="fetchPriorityAttr"
      :class="mediaClasses"
      @load="onImgLoad"
    />
    <div
      v-else-if="resolvedType === 'vector'"
      ref="vectorRef"
      part="base"
      :class="mediaClasses"
      class="e-image__vector"
      aria-hidden="true"
    />
    <div v-if="showSkeleton" part="skeleton" :class="skeletonClasses" role="presentation" aria-hidden="true" />
  </div>
</template>

<style>
:host {
  display: inline-block;
}

/*
  When `type="vector"` the SVG source is fetched and injected as innerHTML
  into `.e-image__vector`. The injected <svg> may carry its own explicit
  width/height attributes, which would otherwise force the vector to render
  at its source dimensions and overflow any container the consumer sizes.
  Making the inline <svg> fill its parent means the vector scales with the
  host — parallel behaviour to `object-fit: contain` on raster images.
*/
.e-image__vector {
  display: block;
  width: 100%;
  height: 100%;
}

.e-image__vector > svg {
  display: block;
  width: 100%;
  height: 100%;
}
</style>
